import * as Sentry from '@sentry/react'
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { getCookie, removeAuthCookie } from '@/helpers/helpers'
import { getTokenFromURL } from '@/utils'
import { endImpersonation, isImpersonating, reloadAt, setImpersonationNotice } from '@/utils/auth'
import { isErrorResponse, unwrapEmbeddedErrorMessage } from '@/utils/error'

export const defaultRequestErrorInterceptor = (error: AxiosError) => Promise.reject(error)

export const requestCookieTokenInterceptor = (config: InternalAxiosRequestConfig) => {
  const token = getCookie('token')
  if (token) {
    config.headers.token = token
  }
  return config
}

export const requestTokenFromUrlInterceptor = (config: InternalAxiosRequestConfig) => {
  const token = getTokenFromURL()
  if (token) {
    config.headers.token = token
  }
  return config
}

export const defaultResponseSuccessInterceptor = (response: AxiosResponse) => response

const extractObjectErrorMessage = (data: unknown): string => {
  if (typeof data !== 'object' || data === null || !('message' in data)) return ''
  const { message } = data as { message: unknown }
  if (typeof message !== 'object' || message === null || !('error' in message)) return ''
  const { error } = message as { error: unknown }
  return typeof error === 'string' ? error : ''
}

// Breadcrumb only, deliberately never `captureException` here — most API errors are already
// caught and handled gracefully by the calling component (e.g. shown as a toast), so capturing
// every one here would make Sentry noisier, not cleaner. This just gives whatever error *does*
// eventually get captured (by a global handler or an explicit `captureException` call) a trail of
// which request(s) failed leading up to it.
const addApiErrorBreadcrumb = (error: AxiosError) => {
  Sentry.addBreadcrumb({
    category: 'api',
    data: { status: error.response?.status },
    level: 'error',
    message: `${error.config?.method?.toUpperCase() ?? ''} ${error.config?.url ?? ''}`.trim(),
  })
}

/**
 * NOTE: the backend returns 403 for two very different situations that share nothing but the
 * status code: (1) the account itself is blocked/deactivated (`permissionModule.js`'s
 * `checkClient`/`checkTrainer`, which set `data: { isBlocked: true }` on the error) - the session
 * really is unusable and the user does need to re-authenticate, same as a 401; (2) a role/policy
 * check rejecting one specific resource (e.g. `isPropertyManager`) while the session is
 * completely valid - `res.error(403)` with no `data` at all in that case. Only the first one
 * should force a logout; the second is a normal per-resource permission denial and must leave
 * the session alone (mirrors how Protect.jsx/withRoleProtection.jsx already treat "not
 * authenticated" and "authenticated but wrong role" as two different outcomes).
 */
const isBlockedAccountError = (data: unknown): boolean =>
  typeof data === 'object' &&
  data !== null &&
  'data' in data &&
  typeof (data as { data: unknown }).data === 'object' &&
  (data as { data: { isBlocked?: unknown } }).data !== null &&
  (data as { data: { isBlocked?: unknown } }).data.isBlocked === true

const parseErrorData = async (error: AxiosError): Promise<unknown> => {
  const errorData = error.response?.data
  if (!(errorData instanceof Blob)) return errorData

  try {
    return JSON.parse(await errorData.text())
  } catch {
    return undefined
  }
}

const extractErrorMessage = (errorData: unknown) => {
  /**
   * NOTE: by default, we write empty string as an error message,
   * cause axios doesn't return any meaningful message for customer
   * Hence, we will be able to write one on place if BE doesn't return it
   */
  const extractedMessage = isErrorResponse(errorData)
    ? errorData.message
    : extractObjectErrorMessage(errorData)

  return extractedMessage ? unwrapEmbeddedErrorMessage(extractedMessage) : extractedMessage
}

const extractErrorCode = (errorData: unknown): string | undefined => {
  const code = (errorData as { code?: unknown } | null | undefined)?.code
  return typeof code === 'string' ? code : undefined
}

const withErrorDetails = async (error: AxiosError) => {
  const errorData = await parseErrorData(error)

  return {
    ...error,
    errorCode: extractErrorCode(errorData),
    message: extractErrorMessage(errorData),
  }
}

export const responseErrorInterceptor = async (error: AxiosError) => {
  addApiErrorBreadcrumb(error)

  const status = error.response?.status

  if (status === 401 || (status === 403 && isBlockedAccountError(error.response?.data))) {
    // A rejection for a token that is no longer the session's (it was swapped by an impersonation
    // start or end while this request was in flight) says nothing about the current session.
    const sentToken = error.config?.headers?.token
    if (sentToken && sentToken !== getCookie('token')) return Promise.reject(error)

    // A rejected impersonated session falls back to the support user's own account, not to login.
    if (isImpersonating() && endImpersonation()) {
      setImpersonationNotice('expired')
      reloadAt('/dashboard/user-panel/profile')
      return Promise.reject(error)
    }

    removeAuthCookie('token')
    removeAuthCookie('role')
    window.location.replace('/#/login')
    return Promise.reject(error)
  }

  return Promise.reject(await withErrorDetails(error))
}

/**
 * NOTE: for public, per-URL-token mini apps (market shop, visitor parking): those flows
 * authenticate via a building/resident token in the URL, not the shared auth cookie, so a 401
 * here says nothing about the main app's cookie session. Clearing that cookie would log out an
 * unrelated, already-authenticated tab sharing the same domain-scoped cookie (see EL-13621).
 */
export const publicResponseErrorInterceptor = async (error: AxiosError) => {
  addApiErrorBreadcrumb(error)

  if (error.response?.status === 401) {
    return Promise.reject(error)
  }

  return Promise.reject(await withErrorDetails(error))
}

/**
 * NOTE: for Places → Integrations (Brivo, Dormakaba, Salto Space, …), which proxy a vendor's own
 * auth handshake — a 401 here means the vendor rejected the credentials, not that the admin's
 * session expired.
 */
export const integrationResponseErrorInterceptor = async (error: AxiosError) => {
  addApiErrorBreadcrumb(error)

  return Promise.reject(await withErrorDetails(error))
}
